import { AppointmentClinicianRow } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import { IClinicianChip } from '@/components/clinicians/ClinicianChip';
import { PatientTags } from '@/components/patients/PatientTags';
import { IBasicAddress, addressInOneLine, basicAddressToAddress } from '@/utils/addresses';
import { Typography } from '@mui/material';

import { IDisciplineChip } from '../../disciplines/DisciplineChip';
import { CaptionLabel } from '../../labels/CaptionLabel';
import { NoneLabel } from '../../labels/NoneLabel';
import { ServiceStatusTag } from '../../services/ServiceStatusTag';
import { HorizontalStack, VerticalStack } from '../../utils/linearLayouts';

export function ServiceDisciplinesLabel({
    disciplines,
}: {
    disciplines: { displayName: string }[];
}) {
    return (
        <CaptionLabel fontSize={'0.8em'}>
            {disciplines.length > 0
                ? disciplines.map((discipline) => discipline.displayName).join(', ')
                : 'Services'}
        </CaptionLabel>
    );
}

export function VisitPatientCell({
    visit,
    showTags = false,
}: {
    visit: {
        patient: {
            fullName: string;
            tags?: { items: { id: string; name: string }[] } | null;
        };
        address: IBasicAddress | null;
    };
    showTags?: boolean;
}) {
    return (
        <VerticalStack>
            <Typography fontSize={'14px'}>{visit.patient.fullName}</Typography>
            {showTags && <PatientTags tags={visit.patient.tags?.items ?? []} fullWidth />}
            {visit.address && (
                <Typography fontSize={'12px'}>
                    {addressInOneLine(basicAddressToAddress(visit.address))}
                </Typography>
            )}
        </VerticalStack>
    );
}

export const ClinicianAppointmentDisciplineServicesCell = ({
    disciplines,
    clinicianAppointments,
}: {
    disciplines: IDisciplineChip[];
    clinicianAppointments: {
        id: string;
        services: {
            id: string;
            name: string;
            isActive?: boolean;
        }[];
    }[];
}) => {
    return (
        <VerticalStack>
            <ServiceDisciplinesLabel disciplines={disciplines} />
            {clinicianAppointments.length > 0 ? (
                clinicianAppointments.map((clinicianAppointment) => (
                    <VerticalStack key={clinicianAppointment.id}>
                        {clinicianAppointment.services.length > 0 ? (
                            clinicianAppointment.services.map((service) => (
                                <HorizontalStack key={service.id} spacing={1} alignItems='center'>
                                    <Typography fontSize={'14px'}>{service.name}</Typography>
                                    <ServiceStatusTag isActive={service.isActive} />
                                </HorizontalStack>
                            ))
                        ) : (
                            <NoneLabel text='No services' fontSize={'14px'} />
                        )}
                    </VerticalStack>
                ))
            ) : (
                <NoneLabel text='No services' fontSize={'14px'} />
            )}
        </VerticalStack>
    );
};

export const ClinicianAppointmentServicesCell = ({
    clinicianAppointments,
}: {
    clinicianAppointments: {
        id: string;
        services: {
            id: string;
            name: string;
            isActive?: boolean;
        }[];
    }[];
}) => {
    return (
        <VerticalStack>
            {clinicianAppointments.length > 0 ? (
                clinicianAppointments.map((clinicianAppointment) => (
                    <VerticalStack key={clinicianAppointment.id}>
                        {clinicianAppointment.services.length > 0 ? (
                            clinicianAppointment.services.map((service) => (
                                <HorizontalStack key={service.id} spacing={1} alignItems='center'>
                                    <Typography fontSize={'14px'}>{service.name}</Typography>
                                    <ServiceStatusTag isActive={service.isActive} />
                                </HorizontalStack>
                            ))
                        ) : (
                            <NoneLabel text='No services' fontSize={'14px'} />
                        )}
                    </VerticalStack>
                ))
            ) : (
                <NoneLabel text='No services' fontSize={'14px'} />
            )}
        </VerticalStack>
    );
};

export const ClinicianAppointmentClinicianCell = ({
    clinicianAppointments,
}: {
    clinicianAppointments: {
        id: string;
        clinician: IClinicianChip | null;
    }[];
}) => {
    return (
        <VerticalStack>
            {clinicianAppointments.length > 0 ? (
                clinicianAppointments.map((clinicianAppointment) => (
                    <AppointmentClinicianRow
                        key={clinicianAppointment.id}
                        clinician={clinicianAppointment.clinician}
                    />
                ))
            ) : (
                <NoneLabel text='Unassigned' fontSize={'14px'} />
            )}
        </VerticalStack>
    );
};
